// src/components/roadmap/RoadmapHero.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Globe, BrainCircuit } from 'lucide-react';

interface HeroData {
  headline: string;
  subheadline: string;
}

interface RoadmapHeroProps {
  heroData: HeroData;
}

export const RoadmapHero: React.FC<RoadmapHeroProps> = ({ heroData }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: 'easeInOut',
      },
    },
  };
  
  const nodeVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 200, damping: 20, delay: 1 } },
  };

  return (
    <section className="bg-slate-900 text-white relative overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[radial-gradient(#2e3c51_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_60%,transparent_100%)]"></div>
      
      <div className="container mx-auto px-4 py-20 md:py-28 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight drop-shadow-lg">
            {heroData.headline}
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-300">
            {heroData.subheadline}
          </p>
        </motion.div>

        {/* The "Living Ecosystem" Visual */}
        <motion.div
          className="mt-16 w-full max-w-2xl mx-auto h-56"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <svg viewBox="0 0 500 200" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="roadmap-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#34d399" />   {/* emerald-400 */}
                <stop offset="50%" stopColor="#38bdf8" />  {/* sky-400 */}
                <stop offset="100%" stopColor="#c084fc" /> {/* purple-400 */}
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Core Pulsing Node */}
            <motion.circle
              cx="250" cy="100" r="10" fill="#f1f5f9"
              animate={{ r: [10, 12, 10], opacity: [0.8, 1, 0.8] }}
              transition={{ duration: 2, repeat: Infinity, repeatType: 'mirror' }}
            />

            {/* Pathways */}
            <motion.path d="M 50,100 C 150,30 200,30 250,100" fill="none" stroke="#475569" strokeWidth="2" variants={pathVariants} />
            <motion.path d="M 250,100 C 300,170 350,170 450,100" fill="none" stroke="#475569" strokeWidth="2" variants={{...pathVariants, visible: {...pathVariants.visible, transition: {...pathVariants.visible.transition, delay: 0.3}}}} />
            <motion.path d="M 125,180 C 175,120 225,120 250,100" fill="none" stroke="#475569" strokeWidth="2" variants={{...pathVariants, visible: {...pathVariants.visible, transition: {...pathVariants.visible.transition, delay: 0.6}}}} />

            {/* Phase Nodes */}
            {[
              { cx: 50, cy: 100, Icon: Layers, color: "#34d399" },
              { cx: 450, cy: 100, Icon: Globe, color: "#38bdf8" },
              { cx: 125, cy: 180, Icon: BrainCircuit, color: "#c084fc" },
            ].map((node, i) => (
              <motion.g key={i} variants={{...nodeVariants, visible: {...nodeVariants.visible, transition: {...nodeVariants.visible.transition, delay: 1 + i * 0.2}}}}>
                <motion.circle cx={node.cx} cy={node.cy} r="18" fill="#1e293b" stroke={node.color} strokeWidth="2" />
                <node.Icon x={node.cx - 12} y={node.cy - 12} size={24} color={node.color} filter="url(#glow)" />
              </motion.g>
            ))}
          </svg>
        </motion.div>
      </div>
    </section>
  );
};